import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";

/** A searchable drop-down over one of the option lists (routes/options.py), for lists that are long and filled from the
 *  jobs themselves (die sizes, die codes, colours ...). Type to search; a value that is not there can be added on the spot. */
export default function OptionCombobox({ optionsKey, value, onChange, testId, placeholder = "Pick or add…" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  // Colour 1-10 only: Yellow/Green/Blue/Neutral etc. per Pantone code, shown next to the code so people can
  // tell the family without knowing the code by heart — a code typed in by hand just shows no category.
  const [categories, setCategories] = useState({});
  // Colour 1-10 only: screen-approximate hex swatch per Pantone code (from the source workbook) — shown as a
  // small colour square next to the code. A code with none (not covered by the workbook yet, or typed by hand)
  // just shows no square.
  const [swatches, setSwatches] = useState({});
  useEffect(() => {
    if (open) api.get("/options").then(({ data }) => setOptions(data[optionsKey] || [])).catch(() => {});
  }, [open, optionsKey]);
  useEffect(() => {
    if (open && optionsKey === "colour" && Object.keys(categories).length === 0) {
      api.get("/options/colour-categories").then(({ data }) => setCategories(data || {})).catch(() => {});
    }
  }, [open, optionsKey, categories]);
  useEffect(() => {
    if (open && optionsKey === "colour" && Object.keys(swatches).length === 0) {
      api.get("/options/colour-swatches").then(({ data }) => setSwatches(data || {})).catch(() => {});
    }
  }, [open, optionsKey, swatches]);

  const text = q.trim();
  const shown = (text ? options.filter((o) => o.toLowerCase().includes(text.toLowerCase())) : options).slice(0, 100);
  const exact = options.some((o) => o.toLowerCase() === text.toLowerCase());
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  const add = async () => {
    try {
      const { data } = await api.post("/options", { key: optionsKey, value: text });
      setOptions(data.values || []);
      pick(text);
      toast.success(`Added "${text}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid={testId}
          className="h-9 w-full justify-between rounded-sm border-neutral-300 text-sm font-normal">
          <span className="flex items-center gap-1.5 min-w-0">
            {value && swatches[value] && (
              <span data-testid={`${testId}-swatch`} className="h-3 w-3 rounded-sm border border-neutral-300 shrink-0" style={{ backgroundColor: swatches[value] }} />
            )}
            <span className={value ? "truncate" : "text-neutral-400"}>{value || placeholder}</span>
          </span>
          <ChevronsUpDown size={14} className="text-neutral-500 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]">
        <Command shouldFilter={false}>
          <CommandInput data-testid={`${testId}-input`} placeholder="Search…" value={q} onValueChange={setQ} />
          <CommandList>
            {shown.length === 0 && !text && <CommandEmpty>Nothing on the list yet: type to add one.</CommandEmpty>}
            <CommandGroup>
              {value && <CommandItem value="__clear__" onSelect={() => pick("")} className="text-neutral-500">Clear</CommandItem>}
              {shown.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)}>
                  <Check size={13} className={`mr-2 ${value === o ? "opacity-100" : "opacity-0"}`} />
                  {swatches[o] && (
                    <span data-testid={`${testId}-swatch-${o}`} className="mr-1.5 h-3 w-3 rounded-sm border border-neutral-300 shrink-0" style={{ backgroundColor: swatches[o] }} />
                  )}
                  <span className="truncate">{o}</span>
                  {categories[o] && <span className="ml-auto pl-2 text-[10px] text-neutral-400 shrink-0">{categories[o]}</span>}
                </CommandItem>
              ))}
              {options.length > shown.length && !text && <div className="px-3 py-1.5 text-[11px] text-neutral-400">Showing {shown.length} of {options.length}: type to search</div>}
            </CommandGroup>
            {text && !exact && (
              <div className="border-t border-neutral-200">
                <button type="button" data-testid={`${testId}-add`} onClick={add} className="w-full text-left px-3 py-2 text-sm text-[#FF5A00] hover:bg-orange-50 flex items-center gap-1.5">
                  <Plus size={13} /> Add “{text}”
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
